import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const COVER_COUNT = 20;
// Bump this whenever a cover .jpg's *content* changes — same filename, new
// pixels, so without a cache-busting query param a browser that already
// fetched the old one (any real visitor, not just a local dev server) just
// keeps showing it from cache indefinitely, no matter how long they wait.
const COVER_VERSION = 11;
const COVERS = Array.from({ length: COVER_COUNT }, (_, i) => `/poem-covers/${String(i + 1).padStart(2, "0")}.jpg?v=${COVER_VERSION}`);

const SLIDE_MS = 3800;
// The swipe/slide motion itself — deliberately fixed at 1.5s, not tied to
// SLIDE_MS. Requested explicitly: a slower, more deliberate glide instead
// of the previous quick 0.7s snap.
const SWIPE_DURATION_S = 1.5;

// The hero's rotating "peek at the library" card — cycles through all 20
// poem covers in order, sliding the next one in from the right every few
// seconds, with that poem's REAL title underneath (pulled from `poems`,
// the same list the library grid below renders — so if a title is ever
// edited in the admin panel, this stays in sync automatically).
//
// Deliberately simple, on request after a fancier version (dynamic
// per-cover height via a `layout` animation, so the box could be both
// always-full and never-cropped) turned out to visibly glitch — two
// slides briefly overlapping at different sizes while framer-motion's
// layout FLIP and the horizontal swipe both fought over size/position at
// once read as an unwanted "zoom", not a clean slide. Back to the classic,
// boring, reliable version instead: one fixed-size box, every cover
// `object-contain`ed into it (the whole image always visible, nothing
// ever cropped off — some covers have caption text baked in right at the
// edge, which is what ruled out object-cover's cropping too — see git log
// 16252ef/44fe05b for that earlier round of this same tradeoff), and
// nothing animating except a plain left-right slide + fade. No layout
// animation, no per-slide resizing, no zoom.
//
// Runs continuously, forever — no pause-on-hover/focus. That used to exist
// (a fairly standard carousel courtesy so a visitor could linger on one
// slide), but in practice it meant the rotation visibly "stopped" any time
// a visitor's cursor merely rested somewhere over the card while reading,
// which read as broken rather than considerate. Requested explicitly: it
// must never stop once it starts.
export default function PoemCoverCarousel({ poems = [] }) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setIndex((i) => (i + 1) % COVER_COUNT), SLIDE_MS);
        return () => clearInterval(id);
    }, []);

    // Fetch the NEXT slide's image as soon as this one is shown, well
    // before the interval actually swaps to it — otherwise the swipe
    // transition starts before the incoming image has even been requested,
    // and it visibly pops in mid-slide on a slow connection. A plain
    // `new Image()` warms the browser's cache; React never touches this.
    useEffect(() => {
        const img = new Image();
        img.src = COVERS[(index + 1) % COVER_COUNT];
    }, [index]);

    const src = COVERS[index];
    const orderIndex = index + 1;
    const poem = poems.find((p) => p.order_index === orderIndex);
    const title = poem?.title || "";

    return (
        <div
            className="relative rounded-[1.75rem] overflow-hidden bg-white"
            data-testid="poem-cover-carousel"
        >
            {/* Sizer — not visible (`invisible`, not `hidden`: still takes up
                real space in normal flow), just here to give this
                position:relative box a real height. Every slide below is
                `absolute inset-0`, so without something in normal flow the
                box would collapse to zero height. Used to be a flat guessed
                px height instead (386/498) — taller than these covers
                actually render at, which is exactly what left the visible
                blank band above/below the image the fixed guess didn't
                account for. Mirroring the same aspect-[800/764] + caption
                strip as the real slide below means this now sizes itself
                off the covers' own real shape (all twenty are 800×761-800,
                so 800/764 is a near-exact fit for all of them) instead of a
                number that was never quite right. */}
            <div className="invisible" aria-hidden="true">
                <div className="w-full aspect-[800/764]" />
                <div className="h-[96px] sm:h-[112px]" />
            </div>

            {/* Image + caption slide together as a single unit — the whole
                card sweeps left, not just the picture with the text
                underneath fading independently. `absolute inset-0` on each
                slide is what lets the outgoing and incoming cards overlap
                during the swipe, both pinned to the same fixed-size box —
                that's only possible because the box's size never changes,
                which is exactly why it's fixed again. */}
            <AnimatePresence initial={false} mode="popLayout">
                <motion.div
                    key={index}
                    initial={{ x: "100%", opacity: 0.6 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: "-100%", opacity: 0.6 }}
                    transition={{ duration: SWIPE_DURATION_S, ease: [0.4, 0, 0.2, 1] }}
                    className="absolute inset-0 flex flex-col"
                >
                    {/* Image zone — same fixed shape on every slide no matter
                        which image is loaded, so the box never bounces
                        between slides — just now that shape is
                        aspect-[800/764] (the covers' own real, near-square
                        ratio — all twenty are 800×761-800px) instead of a
                        guessed px height, so it actually matches what's
                        inside it instead of leaving it visibly letterboxed.
                        `object-contain` stays either way: the whole image is
                        always visible, nothing ever cropped off its edges —
                        this just shrinks how much leftover margin that
                        guarantee ever has to cover in the first place. */}
                    <div className="relative w-full aspect-[800/764] overflow-hidden bg-sand shrink-0">
                        <img
                            src={src}
                            alt={title || "Little Verses poem cover"}
                            className="absolute inset-0 w-full h-full object-contain"
                            loading={index === 0 ? "eager" : "lazy"}
                        />
                    </div>

                    {/* Caption — a FIXED height regardless of title length,
                        so a long title never grows the card taller than a
                        short one — that mismatch was what made the whole
                        card visibly bounce between slides. Text too long to
                        fit clamps to 2 lines rather than pushing outward. */}
                    <div className="relative h-[96px] sm:h-[112px] px-6 flex flex-col items-center justify-center text-center">
                        <p className="font-display text-sm sm:text-base font-bold text-brightGreen">
                            Poem #{String(orderIndex).padStart(2, "0")}
                        </p>
                        <p className="font-display text-2xl sm:text-3xl font-bold text-deepGreen leading-tight mt-1 line-clamp-2">
                            {title}
                        </p>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
